import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/sonner';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import SSOCallback from './components/auth/SSOCallback';
import PortalErrorBoundary from './components/shared/PortalErrorBoundary';
import RoleGate from './components/RoleGate';
import { HOST_PORTAL, pathAllowedOnHost } from './utils/portalHost';
const SetPassword = lazy(() => import('./components/auth/SetPassword'));

// ── M11: React.lazy portal code-splitting ─────────────────────────────────────
// Each portal's JS bundle is only downloaded when the user actually navigates
// to that portal — reduces initial bundle size by ~70%.

// Admin Portal
const AdminLogin    = lazy(() => import('./portals/admin/pages/AdminLogin'));
const AdminLayout   = lazy(() => import('./portals/admin/AdminLayout'));
const AdminDashboard         = lazy(() => import('./portals/admin/pages/AdminDashboard'));
const AdminDealers           = lazy(() => import('./portals/admin/pages/AdminDealers'));
const AdminDealerForm        = lazy(() => import('./portals/admin/pages/AdminDealerForm'));
const AdminLCOs              = lazy(() => import('./portals/admin/pages/AdminLCOs'));
const AdminLCOForm           = lazy(() => import('./portals/admin/pages/AdminLCOForm'));
const AdminUsers             = lazy(() => import('./portals/admin/pages/AdminUsers'));
const AdminPackages          = lazy(() => import('./portals/admin/pages/AdminPackages'));
const AdminRules             = lazy(() => import('./portals/admin/pages/AdminRules'));
const AdminRuleEditor        = lazy(() => import('./portals/admin/pages/AdminRuleEditor'));
const AdminMapping           = lazy(() => import('./portals/admin/pages/AdminMapping'));
const AdminClosedDays        = lazy(() => import('./portals/admin/pages/AdminClosedDays'));
const AdminCampaigns         = lazy(() => import('./portals/admin/pages/AdminCampaigns'));
const AdminAnalytics         = lazy(() => import('./portals/admin/pages/AdminAnalytics'));
const AdminSettings          = lazy(() => import('./portals/admin/pages/AdminSettings'));
const AdminAudit             = lazy(() => import('./portals/admin/pages/AdminAudit'));
const AdminDSAR              = lazy(() => import('./portals/admin/pages/AdminDSAR'));
const AdminBilling           = lazy(() => import('./portals/admin/pages/AdminBilling'));
const AdminFeeds             = lazy(() => import('./portals/admin/pages/AdminFeeds'));
const AdminIntegrations      = lazy(() => import('./portals/admin/pages/AdminIntegrations'));
const AdminNotificationLog   = lazy(() => import('./portals/admin/pages/AdminNotificationLog'));
const AdminConfig            = lazy(() => import('./portals/admin/pages/AdminConfig'));
const AdminTranslations      = lazy(() => import('./portals/admin/pages/AdminTranslations'));
const AdminRBAC              = lazy(() => import('./portals/admin/pages/AdminRBAC'));
const AdminEmailSettings     = lazy(() => import('./portals/admin/pages/AdminEmailSettings'));

// Shared
const ProfilePage = lazy(() => import('./components/layout/ProfilePage'));

// Dealer Portal
const DealerLogin         = lazy(() => import('./portals/dealer/pages/DealerLogin'));
const DealerLayout        = lazy(() => import('./portals/dealer/DealerLayout'));
const DealerDashboard     = lazy(() => import('./portals/dealer/pages/DealerDashboard'));
const DealerInventory     = lazy(() => import('./portals/dealer/pages/DealerInventory'));
const DealerOrders        = lazy(() => import('./portals/dealer/pages/DealerOrders'));
const DealerOrderDetail   = lazy(() => import('./portals/dealer/pages/DealerOrderDetail'));
const DealerCampaigns     = lazy(() => import('./portals/dealer/pages/DealerCampaigns'));
const DealerCampaignWizard = lazy(() => import('./portals/dealer/pages/DealerCampaignWizard'));
const DealerDocuments     = lazy(() => import('./portals/dealer/pages/DealerDocuments'));
const DealerAnalytics     = lazy(() => import('./portals/dealer/pages/DealerAnalytics'));
const DealerPricing       = lazy(() => import('./portals/dealer/pages/DealerPricing'));
const DealerVehiclePhotos = lazy(() => import('./portals/dealer/pages/DealerVehiclePhotos'));
const DealerVehicleForm = lazy(() => import('./portals/dealer/pages/DealerVehicleForm'));
const DealerCampaignReport = lazy(() => import('./portals/dealer/pages/DealerCampaignReport'));

// LCO Portal
const LCOLogin          = lazy(() => import('./portals/lco/pages/LCOLogin'));
const LCOLayout         = lazy(() => import('./portals/lco/LCOLayout'));
const LCODashboard      = lazy(() => import('./portals/lco/pages/LCODashboard'));
const LCOSearch         = lazy(() => import('./portals/lco/pages/LCOSearch'));
const LCORequests       = lazy(() => import('./portals/lco/pages/LCORequests'));
const LCORequestDetail  = lazy(() => import('./portals/lco/pages/LCORequestDetail'));
const LCORequestReview  = lazy(() => import('./portals/lco/pages/LCORequestReview'));
const LCOSaved          = lazy(() => import('./portals/lco/pages/LCOSaved'));
const LCODocuments      = lazy(() => import('./portals/lco/pages/LCODocuments'));
const LCOAnalytics      = lazy(() => import('./portals/lco/pages/LCOAnalytics'));
const LCOVehicleDetail  = lazy(() => import('./portals/lco/pages/LCOVehicleDetail'));
const LCOFavourites     = lazy(() => import('./portals/lco/pages/LCOFavourites'));
const LCOCompare        = lazy(() => import('./portals/lco/pages/LCOCompare'));
const LCOApprovals      = lazy(() => import('./portals/lco/pages/LCOApprovals'));
const LCOCustomers      = lazy(() => import('./portals/lco/pages/LCOCustomers'));
const LCOSettings       = lazy(() => import('./portals/lco/pages/LCOSettings'));
const LCOProfileSettings = lazy(() => import('./portals/lco/pages/LCOProfileSettings'));

// Customer Portal
const CustomerLogin       = lazy(() => import('./portals/customer/pages/CustomerLogin'));
const CustomerLayout      = lazy(() => import('./portals/customer/CustomerLayout'));
const CustomerDashboard   = lazy(() => import('./portals/customer/pages/CustomerDashboard'));
const CustomerOrders      = lazy(() => import('./portals/customer/pages/CustomerOrders'));
const CustomerOrderDetail = lazy(() => import('./portals/customer/pages/CustomerOrderDetail'));
const CustomerDocuments   = lazy(() => import('./portals/customer/pages/CustomerDocuments'));
const CustomerCatalogue   = lazy(() => import('./portals/customer/pages/CustomerCatalogue'));
const CustomerFavourites  = lazy(() => import('./portals/customer/pages/CustomerFavourites'));
const CustomerCompare     = lazy(() => import('./portals/customer/pages/CustomerCompare'));
const CustomerApprovals   = lazy(() => import('./portals/customer/pages/CustomerApprovals'));
const CustomerVehicle     = lazy(() => import('./portals/customer/pages/CustomerVehicle'));
const CustomerRequests    = lazy(() => import('./portals/customer/pages/CustomerRequests'));


// ── Shared portal loading fallback ───────────────────────────────────────────
function PortalLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--surface)' }}>
      <div className="flex flex-col items-center gap-3" style={{ color: 'var(--muted)' }}>
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--brand)', borderTopColor: 'transparent' }} />
        <span className="text-sm">Loading portal…</span>
      </div>
    </div>
  );
}


function ProtectedRoute({ children, requiredRole }) {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  // Wait for the cookie session check — otherwise every refresh / deep link bounces to login
  if (loading) return <PortalLoader />;
  if (!isAuthenticated) return <Navigate to={`/${requiredRole}/login`} replace state={{ from: location }} />;
  if (user?.role !== requiredRole) {
    // On a portal's own address, someone signed in to another portal gets this portal's sign-in
    // (an admin viewing an organisation's portal keeps it).
    if (HOST_PORTAL && !(HOST_PORTAL === 'admin' && user?.viewing_as)) {
      return <Navigate to={`/${requiredRole}/login`} replace state={{ from: location }} />;
    }
    return <Navigate to={`/${user?.role || requiredRole}`} replace />;
  }
  return <PortalErrorBoundary key={requiredRole}>{children}</PortalErrorBoundary>;
}

/** On a portal's own address, another portal's pages send you to this portal. */
function HostGuard({ children }) {
  const location = useLocation();
  if (!pathAllowedOnHost(location.pathname)) return <Navigate to={`/${HOST_PORTAL}`} replace />;
  return children;
}

function RootRedirect() {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <PortalLoader />;
  // A portal's own address (admin-dev / dealer-dev / lco-dev …) opens that portal
  if (HOST_PORTAL) return <Navigate to={isAuthenticated && user.role === HOST_PORTAL ? `/${HOST_PORTAL}` : `/${HOST_PORTAL}/login`} replace />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  return <Navigate to={`/${user.role}`} replace />;
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PortalLoader />}>
          <HostGuard>
          <Routes>
            <Route path="/" element={<RootRedirect />} />

            {/* SSO Callback Routes */}
            <Route path="/set-password"   element={<SetPassword />} />
            <Route path="/auth/google"    element={<SSOCallback provider="google" />} />
            <Route path="/auth/microsoft" element={<SSOCallback provider="microsoft" />} />

            {/* Admin Portal */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="dealers" element={<AdminDealers />} />
              <Route path="dealers/new" element={<AdminDealerForm />} />
              <Route path="dealers/:orgId/edit" element={<AdminDealerForm />} />
              <Route path="lcos" element={<AdminLCOs />} />
              <Route path="lcos/new" element={<AdminLCOForm />} />
              <Route path="lcos/:orgId/edit" element={<AdminLCOForm />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="packages" element={<AdminPackages />} />
              <Route path="rules" element={<AdminRules />} />
              <Route path="mapping" element={<AdminMapping />} />
              <Route path="rules/new" element={<AdminRuleEditor />} />
              <Route path="rules/:ruleId" element={<AdminRuleEditor />} />
              <Route path="closed-days" element={<AdminClosedDays />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="dsar" element={<AdminDSAR />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="feeds" element={<AdminFeeds />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              <Route path="notification-log" element={<AdminNotificationLog />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="config" element={<AdminConfig />} />
              <Route path="translations" element={<AdminTranslations />} />
              <Route path="rbac" element={<AdminRBAC />} />
              <Route path="email-settings" element={<AdminEmailSettings />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Dealer Portal */}
            <Route path="/dealer/login" element={<DealerLogin />} />
            <Route path="/dealer" element={<ProtectedRoute requiredRole="dealer"><DealerLayout /></ProtectedRoute>}>
              <Route index element={<DealerDashboard />} />
              <Route path="inventory" element={<DealerInventory />} />
              <Route path="inventory/:vehicleId/photos" element={<DealerVehiclePhotos />} />
              <Route path="vehicles/:vehicleId/photos" element={<DealerVehiclePhotos />} />
              <Route path="vehicles/new" element={<DealerVehicleForm />} />
              <Route path="vehicles/:vehicleId" element={<DealerVehicleForm />} />
              <Route path="orders" element={<DealerOrders />} />
              <Route path="orders/:id" element={<DealerOrderDetail />} />
              <Route path="campaigns" element={<DealerCampaigns />} />
              <Route path="campaigns/new" element={<RoleGate cap="campaigns"><DealerCampaignWizard /></RoleGate>} />
              <Route path="campaigns/:id/edit" element={<RoleGate cap="campaigns"><DealerCampaignWizard /></RoleGate>} />
              <Route path="campaigns/:id" element={<DealerCampaignReport />} />
              <Route path="pricing" element={<DealerPricing />} />
              <Route path="documents" element={<DealerDocuments />} />
              <Route path="analytics" element={<DealerAnalytics />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* LCO Portal */}
            <Route path="/lco/login" element={<LCOLogin />} />
            <Route path="/lco" element={<ProtectedRoute requiredRole="lco"><LCOLayout /></ProtectedRoute>}>
              <Route index element={<LCODashboard />} />
              <Route path="search" element={<LCOSearch />} />
              <Route path="vehicles/:id" element={<LCOVehicleDetail />} />
              <Route path="requests" element={<LCORequests />} />
              <Route path="requests/:id" element={<LCORequestDetail />} />
              <Route path="requests/:id/review" element={<RoleGate cap="lco_write"><LCORequestReview /></RoleGate>} />
              <Route path="favourites" element={<LCOFavourites />} />
              <Route path="compare" element={<LCOCompare />} />
              <Route path="approvals" element={<LCOApprovals />} />
              <Route path="customers" element={<RoleGate cap="lco_admin"><LCOCustomers /></RoleGate>} />
              <Route path="saved" element={<LCOSaved />} />
              <Route path="alerts" element={<LCOSaved />} />
              <Route path="documents" element={<LCODocuments />} />
              <Route path="analytics" element={<LCOAnalytics />} />
              {/* PRD §2.22: Profile and settings, from the user menu; customer-portal branding (§3) apart */}
              <Route path="settings" element={<LCOProfileSettings />} />
              <Route path="profile" element={<LCOProfileSettings />} />
              <Route path="branding" element={<RoleGate cap="lco_admin"><LCOSettings /></RoleGate>} />
            </Route>

            {/* Customer Portal */}
            <Route path="/customer/login" element={<CustomerLogin />} />
            <Route path="/customer" element={<ProtectedRoute requiredRole="customer"><CustomerLayout /></ProtectedRoute>}>
              <Route index element={<CustomerDashboard />} />
              <Route path="catalogue" element={<CustomerCatalogue />} />
              <Route path="favourites" element={<CustomerFavourites />} />
              <Route path="compare" element={<CustomerCompare />} />
              <Route path="approvals" element={<CustomerApprovals />} />
              <Route path="approvals/:id" element={<CustomerApprovals />} />
              <Route path="vehicles/:id" element={<CustomerVehicle />} />
              <Route path="requests" element={<CustomerRequests />} />
              <Route path="requests/:id" element={<CustomerRequests />} />
              <Route path="orders" element={<CustomerOrders />} />
              <Route path="orders/:id" element={<CustomerOrderDetail />} />
              <Route path="documents" element={<CustomerDocuments />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </HostGuard>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ThemeProvider>
    </GoogleOAuthProvider>
  );
}
